import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined"
import { Grid } from "@mui/material"
import { Button, Snackbar } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { FormikProps } from "formik"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { DecisionNodeDialog } from "../../../../components/dialogs/DecisionNodeDialog"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { PaginatedRuleSetListList } from "../../../../types/generated/api/PaginatedRuleSetListList"
import type { RuleSet } from "../../../../types/generated/api/RuleSet"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { RulesetNodeProps, SelectionDataItemType } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

/**
 * RulesetNode component handles both generic rulesets and decision rulesets in workflows.
 * Supports ruleset selection, external navigation, and decision dialog for decision rulesets.
 * Refactored to use BaseWorkflowNode for consistency and reduced code duplication.
 */
export function RulesetNode(props: Readonly<RulesetNodeProps>): React.ReactElement {
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const rulesets = data?.rulesets
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null
  const ruleset = data?.ruleset ?? null
  const rulesetName = data?.rulesetName ?? ""
  const rules = data?.rules ?? []

  // State hooks
  const [decisionDialogOpen, setDecisionDialogOpen] = useState<boolean>(false)

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "selectionNode",
  })

  // Constants
  const page = 1
  const pageSize = 200

  // Determine node type and title
  const isDecisionNode = data?.nodeType === "DecisionNode"
  const nodeTitle = isDecisionNode ? t("nodeTitles.decision") : t("nodeTitles.ruleset")
  const nodeTypeKey = isDecisionNode ? "DecisionNode" : "RulesetNode"

  // Get node metadata
  const metadata = nodeHelpers.getNodeMetadata(nodeTypeKey as "RulesetNode" | "DecisionNode")

  // Use our custom hook for node deletion
  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Data fetching queries
  const { data: rulesetsData, isLoading: isRulesetsLoading } = useQuery<
    AxiosResponse<PaginatedRuleSetListList>,
    AxiosError
  >(["rulesets", projectId, page, pageSize], () => KonanAPI.fetchRulesets(projectId as string, page, pageSize), {
    enabled:
      !!projectId && !menuView && !readMode && (data?.nodeType === "RulesetNode" || data?.nodeType === "DecisionNode"),
  })

  const currentData = (): PaginatedRuleSetListList | undefined => {
    return rulesetsData?.data ?? rulesets
  }

  const currentDataItemId = (): RuleSet | undefined => {
    return data?.ruleset
  }

  const [selectedValue, setSelectedValue] = useState<SelectionDataItemType | undefined | string>(
    currentDataItemId()
      ? typeof currentDataItemId() === "string"
        ? currentDataItemId()
        : (currentDataItemId() as RuleSet)?.uuid
      : undefined,
  )

  return (
    <Fragment>
      {/* Decision Dialog - Only for DecisionNode */}
      {isDecisionNode && decisionDialogOpen && (
        <DecisionNodeDialog
          rules={rules}
          readMode={readMode !== null}
          rulesetName={rulesetName}
          rulesetId={typeof selectedValue === "string" ? selectedValue : ((ruleset as RuleSet)?.uuid ?? "")}
          isOpen={decisionDialogOpen}
          onClose={() => setDecisionDialogOpen(false)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          workflowFormik={props?.data?.formik as FormikProps<any>}
        />
      )}

      {/* Node Card using BaseWorkflowNode */}
      <BaseWorkflowNode
        id={nodeId as string}
        menuView={menuView}
        readMode={readMode ?? undefined}
        nodeType={nodeTypeKey}
        title={nodeTitle}
        iconProps={{
          element: <RuleOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          className: metadata.iconClassName,
        }}
        width={metadata.width}
        handlesProps={{ showTarget: metadata.showTargetHandle, showSource: metadata.showSourceHandle }}
        onRemove={() =>
          handleRemoveNode(t("notifications.nodeRemoved", { nodeType: isDecisionNode ? "decision" : "ruleset" }))
        }
        customActions={
          !readMode &&
          !menuView &&
          data?.isNodeInLoop && (
            <Snackbar
              title={t("warnings.loop.title")}
              description={t("warnings.loop.description")}
              severity="warning"
              fullWidth
            />
          )
        }
      >
        {/* Node Content */}
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          {/* Data Selection */}
          <Grid item xs={10}>
            {!menuView && !readMode ? (
              <SelectDataMenu
                nodeId={nodeId}
                dataType={nodeTypeKey as "RulesetNode" | "DecisionNode"}
                dataItemId={currentDataItemId()}
                menuView={menuView}
                itemData={currentData()}
                selectedValue={selectedValue as string}
                setSelectedValue={setSelectedValue}
                isDataLoading={isRulesetsLoading}
              />
            ) : (
              <Grid container justifyContent="space-between" alignItems="center">
                <ValueBlock
                  value={rulesetName || t("nodeBodies.noRulesetSelected")}
                  showTooltip={!menuView}
                  size={12}
                />
              </Grid>
            )}
          </Grid>

          {/* Action Button - Decision Dialog for DecisionNode, External Link for RulesetNode */}
          <Grid item xs={2} ml={0.5}>
            <Button
              onClick={
                isDecisionNode
                  ? () => setDecisionDialogOpen(true)
                  : () =>
                      setTimeout(() => {
                        window.open(`/projects/${projectId}/Rulesets`, "_blank")
                      })
              }
              disabled={menuView}
              size={"regular"}
              fullWidth
              data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
              variant="secondary"
            >
              {isDecisionNode ? (
                <OpenInFullIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
              ) : (
                <LaunchOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
              )}
            </Button>
          </Grid>
        </Grid>
      </BaseWorkflowNode>
    </Fragment>
  )
}
